import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, UserCog, CalendarDays, ChevronDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function AttendancePanel({ owner = false, branches = [] }) {
  const [emps, setEmps] = useState([]);
  const [nEmp, setNEmp] = useState({ name: "", branch_id: "", position: "" });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [att, setAtt] = useState([]);
  const [openEmp, setOpenEmp] = useState(null);

  const loadEmps = () => api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
  const loadAtt = () => api.get("/attendance", { params: { month } }).then((r) => setAtt(r.data)).catch(() => {});
  useEffect(() => { loadEmps(); }, []);
  useEffect(() => { loadAtt(); }, [month]);

  const addEmp = async () => {
    if (!nEmp.name.trim()) { toast.error("Isi nama karyawan"); return; }
    if (owner && !nEmp.branch_id) { toast.error("Pilih cabang"); return; }
    try { await api.post("/employees", nEmp); toast.success("Karyawan ditambahkan"); setNEmp({ name: "", branch_id: "", position: "" }); loadEmps(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delEmp = async (id) => { if (!window.confirm("Hapus karyawan ini?")) return; try { await api.delete(`/employees/${id}`); toast.success("Karyawan dihapus"); loadEmps(); } catch (e) { toast.error(apiError(e)); } };

  const histOf = (eid) => att.filter((a) => a.employee_id === eid);
  const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("id-ID", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-");

  return (
    <div className="space-y-5" data-testid="attendance-panel">
      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><Plus className="text-crimson" size={18} /> Tambah Karyawan</div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Input data-testid="emp-name" placeholder="Nama Karyawan" value={nEmp.name} onChange={(e) => setNEmp({ ...nEmp, name: e.target.value })} className="bg-neutral-100 border-neutral-200" />
          {owner ? (
            <Select value={nEmp.branch_id} onValueChange={(v) => setNEmp({ ...nEmp, branch_id: v })}>
              <SelectTrigger data-testid="emp-branch" className="bg-neutral-100 border-neutral-200"><SelectValue placeholder="Cabang" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input disabled value="Cabang Anda" className="bg-neutral-100 border-neutral-200 text-muted-foreground" />
          )}
          <Input data-testid="emp-position" placeholder="Jabatan" value={nEmp.position} onChange={(e) => setNEmp({ ...nEmp, position: e.target.value })} className="bg-neutral-100 border-neutral-200" />
          <Button data-testid="emp-add-btn" onClick={addEmp} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah</Button>
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><UserCog className="text-crimson" size={18} /> Karyawan & Histori Absensi</div>
          <div className="flex items-center gap-2"><CalendarDays size={16} className="text-muted-foreground" /><Input data-testid="att-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-neutral-100 border-neutral-200" /></div>
        </div>
        <div className="space-y-2" data-testid="employees-list">
          {emps.length === 0 && <p className="text-sm text-muted-foreground">Belum ada karyawan.</p>}
          {emps.map((e) => {
            const hist = histOf(e.id);
            const opened = openEmp === e.id;
            return (
              <div key={e.id} data-testid={`emp-row-${e.id}`} className="rounded-xl bg-neutral-100">
                <div className="flex items-center justify-between px-4 py-3">
                  <button onClick={() => setOpenEmp(opened ? null : e.id)} className="flex flex-1 items-center gap-2 text-left">
                    <ChevronDown size={16} className={`text-muted-foreground transition-transform ${opened ? "rotate-180" : ""}`} />
                    <div>
                      <div className="font-semibold text-neutral-900">{e.name} <span className="text-xs font-normal text-muted-foreground">· {e.position || "-"}</span></div>
                      <div className="text-[11px] text-muted-foreground">{e.branch_name} · {hist.length} hari absen ({month})</div>
                    </div>
                  </button>
                  <Button data-testid={`emp-del-${e.id}`} onClick={() => delEmp(e.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
                </div>
                {opened && (
                  <div className="border-t border-neutral-200 p-3" data-testid={`emp-history-${e.id}`}>
                    {hist.length === 0 ? <p className="py-2 text-center text-sm text-muted-foreground">Belum ada absensi bulan ini.</p> : (
                      <div className="space-y-2">
                        {hist.map((a) => (
                          <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-2">
                            <div className="w-32 shrink-0 text-xs font-semibold text-neutral-900">{fmtDate(a.date)}</div>
                            <AttPhoto rec={a} kind="in" label="Masuk" time={fmtTime(a.check_in?.time)} color="text-emerald-600" />
                            <AttPhoto rec={a} kind="out" label="Pulang" time={fmtTime(a.check_out?.time)} color="text-crimson" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">Foto absensi otomatis dihapus sistem setelah 90 hari.</p>
      </Card>
    </div>
  );
}

function AttPhoto({ rec, kind, label, time, color }) {
  const node = kind === "in" ? rec.check_in : rec.check_out;
  return (
    <div className="flex items-center gap-2">
      {node?.photo ? (
        <img src={`${BACKEND}/api/attendance/${rec.id}/photo/${kind}`} alt={label} className="h-12 w-12 rounded-lg object-cover" />
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-neutral-200 text-[10px] text-muted-foreground">-</div>
      )}
      <div className="text-xs"><div className={`font-bold ${color}`}>{label}</div><div className="font-mono-num text-neutral-900">{time}</div></div>
    </div>
  );
}
